import React, { useRef, useState, useCallback } from 'react';
import { Table, Tag, Button, Space, Modal, Select, Row, Col, message } from 'antd';
import { PrinterOutlined, EyeOutlined, FileTextOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import config from '../config';
import dayjs from 'dayjs';

const { Option } = Select;

const Certificates = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuthStore();
    const queryClient = useQueryClient();
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isPrinting, setIsPrinting] = useState(false);
    const printRef = useRef();
    const directPrintRef = useRef();
    const bulkPrintRef = useRef();

    const handlePrintClick = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Certificate-${selectedCertificate?.certificateNumber || 'Certificate'}`,
        onBeforePrint: () => {
            console.log('Starting print...');
            return Promise.resolve();
        },
        onAfterPrint: () => {
            console.log('Print finished');
            setIsPrinting(false);
        },
    });

    const getBulkPrintContent = useCallback(() => {
        return bulkPrintRef.current;
    }, []);

    const handleBulkPrintClick = useReactToPrint({
        content: getBulkPrintContent,
        documentTitle: 'Certificates-Bulk',
        onBeforePrint: () => {
            console.log('Starting bulk print...', bulkPrintRef.current);
            return Promise.resolve();
        },
        onAfterPrint: () => {
            console.log('Bulk print finished');
        },
    });

    const handleDirectPrintClick = useReactToPrint({
        content: () => directPrintRef.current,
        documentTitle: `Certificate-${selectedCertificate?.certificateNumber || 'Certificate'}`,
    });
    const triggerPrint = () => {
        console.log('Print button clicked');
        if (handlePrintClick) {
            setIsPrinting(true);
            setTimeout(() => {
                handlePrintClick();
            }, 100);
        } else {
            console.error('Print handler not available');
        }
    };

    const { data: certificatesData, isLoading } = useQuery('certificates', async () => {
        const response = await api.get('/certificates');
        return response.data.data;
    }, {
        refetchOnWindowFocus: true,
        staleTime: 0, // Always consider data stale
    });

    const { data: coursesData } = useQuery('courses-for-filter', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    });

    const { data: templateData } = useQuery('certificate-template-default', async () => {
        try {
            const response = await api.get('/certificates/template/default');
            console.log('Template data loaded:', response.data.data);
            return response.data.data;
        } catch (error) {
            console.error('Failed to load template:', error);
            return null;
        }
    }, {
        refetchOnWindowFocus: true,
        staleTime: 0, // Always fetch fresh template data
    });

    // Filter certificates by course
    const filteredCertificates = selectedCourse
        ? certificatesData?.filter(cert => cert.course?._id === selectedCourse)
        : certificatesData;

    const handlePreview = (record) => {
        setSelectedCertificate(record);
        setPreviewModalVisible(true);
    };
    const handlePrintSelected = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('pleaseSelectCertificates') || 'Please select certificates to print');
            return;
        }

        // Validate that we have certificates to print
        const certsToPrint = certificatesData?.filter(cert =>
            selectedRowKeys.includes(cert._id)
        ) || [];

        if (certsToPrint.length === 0) {
            message.error('No certificates found to print');
            return;
        }

        console.log('Preparing to print certificates:', certsToPrint.length);

        // Wait for DOM to be ready
        setTimeout(() => {
            if (bulkPrintRef.current) {
                // Open new window and load all certificates
                const printWindow = window.open('', '_blank');
                printWindow.document.write('<html><head><title>Certificates - Bulk Print</title>');
                printWindow.document.write('<style>');
                printWindow.document.write('@media print { @page { size: A4 landscape; margin: 0; } body { margin: 0; padding: 0; } }');
                printWindow.document.write('.certificate-page { page-break-after: always; page-break-inside: avoid; }');
                printWindow.document.write('.certificate-page:last-child { page-break-after: auto; }');
                printWindow.document.write('</style>');
                printWindow.document.write('</head><body>');
                printWindow.document.write(bulkPrintRef.current.innerHTML);
                printWindow.document.write('</body></html>');
                printWindow.document.close();

                // Trigger print after content is loaded
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                }, 1000);
            } else {
                message.error('Print container not ready. Please try again.');
            }
        }, 300);
    };

    const handleSelectAll = () => {
        if (filteredCertificates) {
            const allIds = filteredCertificates.map(cert => cert._id);
            setSelectedRowKeys(allIds);
        }
    };

    const handleDeselectAll = () => {
        setSelectedRowKeys([]);
    };

    const deleteMutation = useMutation(
        (courseId) => api.delete(`/certificates/course/${courseId}`),
        {
            onSuccess: (response) => {
                message.success(response.data.message || 'Certificates deleted successfully');
                queryClient.invalidateQueries('certificates');
                setSelectedCourse(null);
                setSelectedRowKeys([]);
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to delete certificates');
            },
        }
    );

    const handleDeleteCourseCertificates = () => {
        if (!selectedCourse) {
            message.warning('Please select a course first');
            return;
        }

        const courseName = coursesData?.find(c => c._id === selectedCourse)?.name;
        const certificateCount = filteredCertificates?.length || 0;

        Modal.confirm({
            title: t('deleteCertificates') || 'Delete All Certificates',
            content: `Are you sure you want to delete all ${certificateCount} certificate(s) for "${courseName}"? This action cannot be undone.`,
            okText: t('delete') || 'Delete',
            okType: 'danger',
            cancelText: t('cancel') || 'Cancel',
            onOk: () => {
                deleteMutation.mutate(selectedCourse);
            },
        });
    };

    const selectedCertificates = certificatesData?.filter(cert =>
        selectedRowKeys.includes(cert._id)
    ) || [];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const columns = [
        { title: t('certificateNo'), dataIndex: 'certificateNumber', key: 'certificateNumber' },
        { title: t('student'), key: 'student', render: (_, r) => `${r.student?.firstName} ${r.student?.lastName}` },
        { title: t('course'), key: 'course', render: (_, r) => r.course?.name },
        { title: t('issueDate'), dataIndex: 'issueDate', key: 'issueDate', render: (date) => dayjs(date).format('YYYY-MM-DD') },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color="blue">{status?.toUpperCase()}</Tag>,
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(record)}
                        size="small"
                    >
                        {t('preview')}
                    </Button>
                    <Button
                        icon={<PrinterOutlined />}
                        type="primary"
                        onClick={() => {
                            setSelectedCertificate(record);
                            setTimeout(() => {
                                if (directPrintRef.current) {
                                    const printWindow = window.open('', '_blank');
                                    printWindow.document.write('<html><head><title>Certificate</title>');
                                    printWindow.document.write('<style>@media print { @page { margin: 0; } body { margin: 0; padding: 0; } }</style>');
                                    printWindow.document.write('</head><body>');
                                    printWindow.document.write(directPrintRef.current.innerHTML);
                                    printWindow.document.write('</body></html>');
                                    printWindow.document.close();
                                    setTimeout(() => {
                                        printWindow.print();
                                        printWindow.close();
                                    }, 500);
                                }
                            }, 100);
                        }}
                        size="small"
                    >
                        {t('print')}
                    </Button>
                </Space>
            ),
        },
    ];

    const CertificateTemplate = ({ certificate }) => {
        if (!certificate) return null;

        // Always use the latest default template, not the stored one
        const template = templateData || certificate.template;
        console.log('Using template:', template);
        console.log('Certificate data:', certificate);

        if (!template) return null;

        const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
        const isLandscape = template.design?.orientation === 'landscape';

        console.log('Orientation:', template.design?.orientation, 'isLandscape:', isLandscape);

        // Process template text with variables
        const processText = (text) => {
            if (!text) return '';
            return text
                .replace('{{studentName}}', `${certificate.student?.firstName} ${certificate.student?.lastName}`)
                .replace('{{courseName}}', certificate.course?.name)
                .replace('{{completionDate}}', dayjs(certificate.completionDate).format('MMMM DD, YYYY'))
                .replace('{{issueDate}}', dayjs(certificate.issueDate).format('MMMM DD, YYYY'))
                .replace('{{certificateNumber}}', certificate.certificateNumber)
                .replace('{{grade}}', certificate.grade || 'Pass');
        };

        // Process text with styled placeholders and HTML content
        const processTextWithStyles = (text) => {
            if (!text) return null;

            const placeholderMap = {
                '{{studentName}}': `${certificate.student?.firstName} ${certificate.student?.lastName}`,
                '{{courseName}}': certificate.course?.name,
                '{{completionDate}}': dayjs(certificate.completionDate).format('MMMM DD, YYYY'),
                '{{issueDate}}': dayjs(certificate.issueDate).format('MMMM DD, YYYY'),
                '{{certificateNumber}}': certificate.certificateNumber,
                '{{grade}}': certificate.grade || 'Pass',
                '{{duration}}': certificate.duration || '',
                '{{instructorName}}': certificate.instructorName || '',
                '{{academyName}}': template.header?.instituteName || user?.academy?.name || ''
            };

            // Replace placeholders with actual values
            let processedText = text;
            Object.keys(placeholderMap).forEach(key => {
                const value = placeholderMap[key];
                const style = template.placeholderStyles?.[key];

                if (style && Object.keys(style).length > 0) {
                    // Apply custom styles to placeholder
                    const styleString = [
                        style.fontFamily ? `font-family: ${style.fontFamily}` : '',
                        style.fontSize ? `font-size: ${style.fontSize}px` : '',
                        style.color ? `color: ${style.color}` : '',
                        style.fontWeight ? `font-weight: ${style.fontWeight}` : '',
                        style.fontStyle ? `font-style: ${style.fontStyle}` : '',
                        style.textDecoration ? `text-decoration: ${style.textDecoration}` : '',
                        style.textTransform ? `text-transform: ${style.textTransform}` : ''
                    ].filter(Boolean).join('; ');

                    processedText = processedText.replace(
                        new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'),
                        `<span style="${styleString}">${value}</span>`
                    );
                } else {
                    // No custom style, just replace
                    processedText = processedText.replace(
                        new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'),
                        value
                    );
                }
            });

            // Return HTML content using dangerouslySetInnerHTML
            return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
        };

        // Corner decoration SVG patterns
        const getCornerDecoration = (type, color, size) => {
            const patterns = {
                floral: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,0 Q25,0 50,25 Q75,50 100,50 Q100,25 100,0 M0,0 Q0,25 0,50 Q25,75 50,100 Q25,100 0,100" 
                    fill="${color}" opacity="0.3"/>
                    <circle cx="30" cy="30" r="8" fill="${color}"/>
                    <circle cx="20" cy="20" r="5" fill="${color}"/>
                </svg>`,
                geometric: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="0,0 100,0 50,50" fill="${color}" opacity="0.3"/>
                    <rect x="0" y="0" width="30" height="30" fill="${color}"/>
                    <rect x="35" y="35" width="20" height="20" fill="${color}" opacity="0.5"/>
                </svg>`,
                ornamental: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,50 Q0,0 50,0 L0,0 Z" fill="${color}" opacity="0.3"/>
                    <circle cx="25" cy="25" r="15" fill="none" stroke="${color}" stroke-width="2"/>
                    <path d="M10,10 L40,40 M10,40 L40,10" stroke="${color}" stroke-width="2"/>
                </svg>`,
                islamic: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,0 Q50,50 0,100 Q50,50 100,100 Q50,50 100,0 Q50,50 0,0" 
                    fill="none" stroke="${color}" stroke-width="2" opacity="0.5"/>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="${color}" stroke-width="2"/>
                    <path d="M25,5 L25,45 M5,25 L45,25" stroke="${color}" stroke-width="2"/>
                </svg>`,
                arabesque: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,0 Q40,10 50,40 Q60,10 100,0" fill="none" stroke="${color}" stroke-width="2"/>
                    <path d="M0,0 Q10,40 40,50 Q10,60 0,100" fill="none" stroke="${color}" stroke-width="2"/>
                    <circle cx="30" cy="30" r="12" fill="none" stroke="${color}" stroke-width="1.5"/>
                    <path d="M15,15 Q30,20 45,15 Q40,30 45,45 Q30,40 15,45 Q20,30 15,15" fill="${color}" opacity="0.2"/>
                </svg>`,
                baroque: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,20 Q20,0 40,20 T80,20" fill="none" stroke="${color}" stroke-width="2"/>
                    <path d="M20,0 Q0,20 20,40 T20,80" fill="none" stroke="${color}" stroke-width="2"/>
                    <ellipse cx="35" cy="35" rx="15" ry="20" fill="${color}" opacity="0.3"/>
                    <path d="M10,10 C20,5 30,15 25,25 C20,20 15,15 10,10" fill="${color}"/>
                </svg>`,
                celtic: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,25 Q25,0 50,25 Q25,50 0,25" fill="none" stroke="${color}" stroke-width="3"/>
                    <path d="M25,0 Q50,25 25,50 Q0,25 25,0" fill="none" stroke="${color}" stroke-width="3"/>
                    <circle cx="25" cy="25" r="8" fill="${color}" opacity="0.4"/>
                    <path d="M15,15 L35,35 M15,35 L35,15" stroke="${color}" stroke-width="2" opacity="0.6"/>
                </svg>`,
                art_deco: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="0,0 50,0 25,25" fill="${color}"/>
                    <polygon points="0,0 0,50 25,25" fill="${color}" opacity="0.7"/>
                    <rect x="30" y="5" width="15" height="15" fill="${color}" opacity="0.5"/>
                    <rect x="5" y="30" width="15" height="15" fill="${color}" opacity="0.5"/>
                    <line x1="0" y1="0" x2="50" y2="50" stroke="${color}" stroke-width="2"/>
                </svg>`,
                vintage: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,30 C10,10 30,0 50,10 L0,10 Z" fill="${color}" opacity="0.3"/>
                    <path d="M10,0 C10,20 0,40 20,50 L10,0 Z" fill="${color}" opacity="0.3"/>
                    <circle cx="25" cy="25" r="6" fill="${color}"/>
                    <circle cx="15" cy="15" r="4" fill="${color}" opacity="0.6"/>
                    <path d="M5,5 Q15,10 25,5" fill="none" stroke="${color}" stroke-width="1.5"/>
                </svg>`,
                modern_curves: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,0 Q30,30 0,60" fill="none" stroke="${color}" stroke-width="3" opacity="0.8"/>
                    <path d="M0,0 Q30,30 60,0" fill="none" stroke="${color}" stroke-width="3" opacity="0.8"/>
                    <circle cx="20" cy="20" r="10" fill="${color}" opacity="0.3"/>
                    <circle cx="35" cy="35" r="5" fill="${color}"/>
                </svg>`,
                elegant_swirl: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,50 Q20,20 50,0" fill="none" stroke="${color}" stroke-width="2.5"/>
                    <path d="M50,0 Q80,20 100,50" fill="none" stroke="${color}" stroke-width="2.5" opacity="0.6"/>
                    <path d="M0,30 Q15,15 30,0" fill="none" stroke="${color}" stroke-width="2" opacity="0.4"/>
                    <circle cx="25" cy="25" r="3" fill="${color}"/>
                </svg>`,
                royal: `<svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="0,0 50,25 0,50" fill="${color}" opacity="0.4"/>
                    <polygon points="0,0 25,50 50,0" fill="${color}" opacity="0.4"/>
                    <path d="M10,10 L30,10 L20,30 Z" fill="${color}"/>
                    <circle cx="20" cy="15" r="5" fill="none" stroke="${color}" stroke-width="2"/>
                    <line x1="0" y1="0" x2="40" y2="40" stroke="${color}" stroke-width="1.5" opacity="0.6"/>
                </svg>`
            };
            return patterns[type] || patterns.floral;
        };

        return (
            <>
                <style>
                    {`
                        @media print {
                            @page {
                                size: A4 ${isLandscape ? 'landscape' : 'portrait'};
                                margin: 0;
                            }
                            body {
                                margin: 0;
                                padding: 0;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            * {
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                        }
                        @media screen {
                            .certificate-preview-wrapper {
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                padding: 20px;
                                background-color: #f0f0f0;
                            }
                        }
                    `}
                </style>
                <div style={{
                    width: isLandscape ? '297mm' : '210mm',
                    height: isLandscape ? '210mm' : '297mm',
                    padding: template.design?.layoutSettings?.contentPadding !== undefined
                        ? `${template.design?.layoutSettings?.contentPadding}px`
                        : '15mm',
                    margin: '0 auto',
                    backgroundColor: template.design?.backgroundColor || '#fff',
                    backgroundImage: template.design?.backgroundImage
                        ? `url(${config.baseUrl}${template.design.backgroundImage})`
                        : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0',
                    boxShadow: (() => {
                        const borderStyle = template.design?.borderStyle || 'single';
                        const borderColor = template.design?.borderColor || '#1890ff';
                        const secondaryColor = template.design?.secondaryBorderColor || '#DAA520';

                        if (borderStyle === 'none') return 'none';
                        if (borderStyle === 'single') return `inset 0 0 0 2px ${borderColor}`;
                        if (borderStyle === 'double') return `inset 0 0 0 2px ${borderColor}, inset 0 0 0 6px ${secondaryColor}, inset 0 0 0 8px ${borderColor}`;
                        if (borderStyle === 'triple') return `inset 0 0 0 2px ${borderColor}, inset 0 0 0 5px ${secondaryColor}, inset 0 0 0 7px ${borderColor}, inset 0 0 0 10px ${secondaryColor}, inset 0 0 0 12px ${borderColor}`;
                        if (borderStyle === 'ornate') return `inset 0 0 0 1px ${borderColor}, inset 0 0 0 3px ${secondaryColor}, inset 0 0 0 4px ${borderColor}, inset 0 0 0 8px ${secondaryColor}, inset 0 0 0 10px ${borderColor}`;
                        return `inset 0 0 0 2px ${borderColor}`;
                    })(),
                    border: (() => {
                        const borderStyle = template.design?.borderStyle || 'single';
                        const borderColor = template.design?.borderColor || '#1890ff';
                        if (borderStyle === 'dashed') return `3px dashed ${borderColor}`;
                        return 'none';
                    })(),
                    direction: isRTL ? 'rtl' : 'ltr',
                    fontFamily: template.body?.text?.font?.family || 'Arial, sans-serif',
                    position: 'relative',
                    boxSizing: 'border-box'
                }}>
                    {/* Full Frame Decorations */}
                    {template.design?.frameDecoration?.enabled && (() => {
                        const frameStyle = template.design.frameDecoration.style || 'vintage_border';
                        const frameColor = template.design.frameDecoration.color || '#8B4513';
                        const frameThickness = template.design.frameDecoration.thickness || 2;

                        const framePatterns = {
                            vintage_border: (
                                <>
                                    <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                    <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M7,7 Q7,12 10,12 Q12,12 12,10 Q12,7 7,7 M7,7 L12,12 M10,7 Q10,10 7,10" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M93,7 Q93,12 90,12 Q88,12 88,10 Q88,7 93,7 M93,7 L88,12 M90,7 Q90,10 93,10" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M7,93 Q7,88 10,88 Q12,88 12,90 Q12,93 7,93 M7,93 L12,88 M10,93 Q10,90 7,90" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M93,93 Q93,88 90,88 Q88,88 88,90 Q88,93 93,93 M93,93 L88,88 M90,93 Q90,90 93,90" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M10,10 Q8,13 10,16 Q13,18 16,16 Q18,13 16,10 Q13,8 10,10" fill={frameColor} opacity="0.2" />
                                    <path d="M90,10 Q92,13 90,16 Q87,18 84,16 Q82,13 84,10 Q87,8 90,10" fill={frameColor} opacity="0.2" />
                                    <path d="M10,90 Q8,87 10,84 Q13,82 16,84 Q18,87 16,90 Q13,92 10,90" fill={frameColor} opacity="0.2" />
                                    <path d="M90,90 Q92,87 90,84 Q87,82 84,84 Q82,87 84,90 Q87,92 90,90" fill={frameColor} opacity="0.2" />
                                    <circle cx="50" cy="4" r="1.5" fill={frameColor} />
                                    <circle cx="50" cy="96" r="1.5" fill={frameColor} />
                                    <circle cx="4" cy="50" r="1.5" fill={frameColor} />
                                    <circle cx="96" cy="50" r="1.5" fill={frameColor} />
                                </>
                            ),
                            elegant_frame: (
                                <>
                                    <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />
                                    <path d="M8,5 Q10,3 12,5 Q14,3 16,5 Q18,3 20,5 Q22,3 24,5 Q26,3 28,5 Q30,3 32,5 Q34,3 36,5 Q38,3 40,5 Q42,3 44,5 Q46,3 48,5 Q50,3 52,5 Q54,3 56,5 Q58,3 60,5 Q62,3 64,5 Q66,3 68,5 Q70,3 72,5 Q74,3 76,5 Q78,3 80,5 Q82,3 84,5 Q86,3 88,5 Q90,3 92,5" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M8,95 Q10,97 12,95 Q14,97 16,95 Q18,97 20,95 Q22,97 24,95 Q26,97 28,95 Q30,97 32,95 Q34,97 36,95 Q38,97 40,95 Q42,97 44,95 Q46,97 48,95 Q50,97 52,95 Q54,97 56,95 Q58,97 60,95 Q62,97 64,95 Q66,97 68,95 Q70,97 72,95 Q74,97 76,95 Q78,97 80,95 Q82,97 84,95 Q86,97 88,95 Q90,97 92,95" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M5,8 Q3,10 5,12 Q3,14 5,16 Q3,18 5,20 Q3,22 5,24 Q3,26 5,28 Q3,30 5,32 Q3,34 5,36 Q3,38 5,40 Q3,42 5,44 Q3,46 5,48 Q3,50 5,52 Q3,54 5,56 Q3,58 5,60 Q3,62 5,64 Q3,66 5,68 Q3,70 5,72 Q3,74 5,76 Q3,78 5,80 Q3,82 5,84 Q3,86 5,88 Q3,90 5,92" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <path d="M95,8 Q97,10 95,12 Q97,14 95,16 Q97,18 95,20 Q97,22 95,24 Q97,26 95,28 Q97,30 95,32 Q97,34 95,36 Q97,38 95,40 Q97,42 95,44 Q97,46 95,48 Q97,50 95,52 Q97,54 95,56 Q97,58 95,60 Q97,62 95,64 Q97,66 95,68 Q97,70 95,72 Q97,74 95,76 Q97,78 95,80 Q97,82 95,84 Q97,86 95,88 Q97,90 95,92" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.6} />
                                    <circle cx="8" cy="8" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="92" cy="8" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="8" cy="92" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="92" cy="92" r="2" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                </>
                            ),
                            ornate_full: (
                                <>
                                    <rect x="1" y="1" width="98" height="98" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                    <rect x="3" y="3" width="94" height="94" fill="none" stroke={frameColor} strokeWidth={frameThickness} />
                                    <rect x="5.5" y="5.5" width="89" height="89" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <rect x="8" y="8" width="84" height="84" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M10,10 L15,10 L15,15 L10,15 Z M12.5,10 L12.5,15 M10,12.5 L15,12.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M90,10 L85,10 L85,15 L90,15 Z M87.5,10 L87.5,15 M85,12.5 L90,12.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M10,90 L15,90 L15,85 L10,85 Z M12.5,85 L12.5,90 M10,87.5 L15,87.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M90,90 L85,90 L85,85 L90,85 Z M87.5,85 L87.5,90 M85,87.5 L90,87.5" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M12,12 Q10,15 12,18 Q15,16 18,18 Q16,15 18,12 Q15,14 12,12" fill={frameColor} opacity="0.25" />
                                    <path d="M88,12 Q90,15 88,18 Q85,16 82,18 Q84,15 82,12 Q85,14 88,12" fill={frameColor} opacity="0.25" />
                                    <path d="M12,88 Q10,85 12,82 Q15,84 18,82 Q16,85 18,88 Q15,86 12,88" fill={frameColor} opacity="0.25" />
                                    <path d="M88,88 Q90,85 88,82 Q85,84 82,82 Q84,85 82,88 Q85,86 88,88" fill={frameColor} opacity="0.25" />
                                    <circle cx="50" cy="3" r="2" fill={frameColor} />
                                    <circle cx="50" cy="97" r="2" fill={frameColor} />
                                    <circle cx="3" cy="50" r="2" fill={frameColor} />
                                    <circle cx="97" cy="50" r="2" fill={frameColor} />
                                    <path d="M25,3 L27,5 L25,7 L23,5 Z" fill={frameColor} />
                                    <path d="M75,3 L77,5 L75,7 L73,5 Z" fill={frameColor} />
                                    <path d="M25,97 L27,95 L25,93 L23,95 Z" fill={frameColor} />
                                    <path d="M75,97 L77,95 L75,93 L73,95 Z" fill={frameColor} />
                                </>
                            ),
                            art_deco_frame: (
                                <>
                                    <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.3} />
                                    <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <path d="M5,5 L20,5 L20,20 L5,20 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <path d="M8,8 Q12.5,12.5 8,17 M10,8 Q12.5,12.5 10,17 M12,8 Q12.5,12.5 12,17 M14,8 Q12.5,12.5 14,17 M16,8 Q12.5,12.5 16,17" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />
                                    <path d="M95,5 L80,5 L80,20 L95,20 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <path d="M92,8 Q87.5,12.5 92,17 M90,8 Q87.5,12.5 90,17 M88,8 Q87.5,12.5 88,17 M86,8 Q87.5,12.5 86,17 M84,8 Q87.5,12.5 84,17" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />
                                    <path d="M5,95 L20,95 L20,80 L5,80 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <path d="M8,92 Q12.5,87.5 8,83 M10,92 Q12.5,87.5 10,83 M12,92 Q12.5,87.5 12,83 M14,92 Q12.5,87.5 14,83 M16,92 Q12.5,87.5 16,83" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />
                                    <path d="M95,95 L80,95 L80,80 L95,80 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <path d="M92,92 Q87.5,87.5 92,83 M90,92 Q87.5,87.5 90,83 M88,92 Q87.5,87.5 88,83 M86,92 Q87.5,87.5 86,83 M84,92 Q87.5,87.5 84,83" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.4} />
                                    <line x1="20" y1="5" x2="30" y2="15" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <line x1="80" y1="5" x2="70" y2="15" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <line x1="20" y1="95" x2="30" y2="85" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <line x1="80" y1="95" x2="70" y2="85" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                </>
                            ),
                            baroque_frame: (
                                <>
                                    <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.4} />
                                    <rect x="4.5" y="4.5" width="91" height="91" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <rect x="7" y="7" width="86" height="86" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M10,10 Q8,12 8,15 Q8,18 10,20 Q12,22 15,22 Q18,22 20,20 Q22,18 22,15 Q22,12 20,10 Q18,8 15,8 Q12,8 10,10" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <path d="M90,10 Q92,12 92,15 Q92,18 90,20 Q88,22 85,22 Q82,22 80,20 Q78,18 78,15 Q78,12 80,10 Q82,8 85,8 Q88,8 90,10" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <path d="M10,90 Q8,88 8,85 Q8,82 10,80 Q12,78 15,78 Q18,78 20,80 Q22,82 22,85 Q22,88 20,90 Q18,92 15,92 Q12,92 10,90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <path d="M90,90 Q92,88 92,85 Q92,82 90,80 Q88,78 85,78 Q82,78 80,80 Q78,82 78,85 Q78,88 80,90 Q82,92 85,92 Q88,92 90,90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <path d="M15,10 Q12,12 12,15 M15,10 Q18,12 18,15" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                    <path d="M85,10 Q88,12 88,15 M85,10 Q82,12 82,15" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                    <path d="M15,90 Q12,88 12,85 M15,90 Q18,88 18,85" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                    <path d="M85,90 Q88,88 88,85 M85,90 Q82,88 82,85" stroke={frameColor} fill="none" strokeWidth={frameThickness * 0.5} />
                                    <path d="M35,3 Q40,5 45,3 M55,3 Q60,5 65,3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M35,97 Q40,95 45,97 M55,97 Q60,95 65,97" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M3,35 Q5,40 3,45 M3,55 Q5,60 3,65" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M97,35 Q95,40 97,45 M97,55 Q95,60 97,65" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                </>
                            ),
                            modern_frame: (
                                <>
                                    <rect x="4" y="4" width="92" height="92" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.5} />
                                    <line x1="4" y1="20" x2="4" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="4" y1="4" x2="20" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="96" y1="20" x2="96" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="96" y1="4" x2="80" y2="4" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="4" y1="80" x2="4" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="4" y1="96" x2="20" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="96" y1="80" x2="96" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <line x1="96" y1="96" x2="80" y2="96" stroke={frameColor} strokeWidth={frameThickness * 2.5} />
                                    <circle cx="50" cy="6" r="1.5" fill={frameColor} />
                                    <circle cx="50" cy="94" r="1.5" fill={frameColor} />
                                    <circle cx="6" cy="50" r="1.5" fill={frameColor} />
                                    <circle cx="94" cy="50" r="1.5" fill={frameColor} />
                                </>
                            ),
                            islamic_frame: (
                                <>
                                    <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />
                                    <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M10,12 L12,10 L14,12 L12,14 Z M12,8 L14,10 L12,12 L10,10 Z M16,10 L14,12 L12,10 L14,8 Z M14,14 L12,12 L14,10 L16,12 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="12" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M90,12 L88,10 L86,12 L88,14 Z M88,8 L86,10 L88,12 L90,10 Z M84,10 L86,12 L88,10 L86,8 Z M86,14 L88,12 L86,10 L84,12 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="88" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M10,88 L12,90 L14,88 L12,86 Z M12,92 L14,90 L12,88 L10,90 Z M16,90 L14,88 L12,90 L14,92 Z M14,86 L12,88 L14,90 L16,88 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="12" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M90,88 L88,90 L86,88 L88,86 Z M88,92 L86,90 L88,88 L90,90 Z M84,90 L86,88 L88,90 L86,92 Z M86,86 L88,88 L86,90 L84,88 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="88" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <rect x="7.5" y="7.5" width="85" height="85" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} strokeDasharray="4,2" />
                                    <path d="M48,3 L50,5 L52,3 L50,1 Z" fill={frameColor} />
                                    <path d="M48,97 L50,95 L52,97 L50,99 Z" fill={frameColor} />
                                    <path d="M3,48 L5,50 L3,52 L1,50 Z" fill={frameColor} />
                                    <path d="M97,48 L95,50 L97,52 L99,50 Z" fill={frameColor} />
                                </>
                            ),
                            modern_islamic: (
                                <>
                                    <rect x="3" y="3" width="94" height="94" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.1} />
                                    <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} strokeDasharray="6,3" />
                                    <path d="M10,10 L15,6 L20,10 L15,14 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M90,10 L94,15 L90,20 L86,15 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M10,90 L6,85 L10,80 L14,85 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M90,90 L85,94 L80,90 L85,86 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <circle cx="50" cy="4" r="2" fill={frameColor} />
                                    <circle cx="50" cy="96" r="2" fill={frameColor} />
                                    <circle cx="4" cy="50" r="2" fill={frameColor} />
                                    <circle cx="96" cy="50" r="2" fill={frameColor} />
                                </>
                            ),
                            classic_islamic: (
                                <>
                                    <rect x="2" y="2" width="96" height="96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.4} />
                                    <rect x="5" y="5" width="90" height="90" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.8} />
                                    <path d="M12,12 L16,10 L20,12 L18,16 L20,20 L16,18 L12,20 L14,16 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M88,12 L90,16 L88,20 L84,18 L80,20 L82,16 L80,12 L84,14 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M12,88 L14,84 L12,80 L16,82 L20,80 L18,84 L20,88 L16,86 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M88,88 L84,86 L80,88 L82,84 L80,80 L84,82 L88,80 L86,84 Z" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M50,3 L52,6 L50,9 L48,6 Z" fill={frameColor} />
                                    <path d="M50,97 L52,94 L50,91 L48,94 Z" fill={frameColor} />
                                    <path d="M3,50 L6,52 L9,50 L6,48 Z" fill={frameColor} />
                                    <path d="M97,50 L94,52 L91,50 L94,48 Z" fill={frameColor} />
                                </>
                            ),
                            modern_ornate: (
                                <>
                                    <rect x="1.5" y="1.5" width="97" height="97" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.8} />
                                    <rect x="3.5" y="3.5" width="93" height="93" fill="none" stroke={frameColor} strokeWidth={frameThickness * 1.2} />
                                    <rect x="6" y="6" width="88" height="88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.6} />
                                    <path d="M8,4 Q10,2 12,4 Q14,6 16,4 Q18,2 20,4 Q22,6 24,4 Q26,2 28,4 Q30,6 32,4 Q34,2 36,4 Q38,6 40,4 Q42,2 44,4 Q46,6 48,4 Q50,2 52,4 Q54,6 56,4 Q58,2 60,4 Q62,6 64,4 Q66,2 68,4 Q70,6 72,4 Q74,2 76,4 Q78,6 80,4 Q82,2 84,4 Q86,6 88,4 Q90,2 92,4" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M10,2.5 Q12,1 14,2.5 M18,2.5 Q20,1 22,2.5 M26,2.5 Q28,1 30,2.5 M34,2.5 Q36,1 38,2.5 M42,2.5 Q44,1 46,2.5 M50,2.5 Q52,1 54,2.5 M58,2.5 Q60,1 62,2.5 M66,2.5 Q68,1 70,2.5 M74,2.5 Q76,1 78,2.5 M82,2.5 Q84,1 86,2.5 M90,2.5 Q92,1 94,2.5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M8,96 Q10,98 12,96 Q14,94 16,96 Q18,98 20,96 Q22,94 24,96 Q26,98 28,96 Q30,94 32,96 Q34,98 36,96 Q38,94 40,96 Q42,98 44,96 Q46,94 48,96 Q50,98 52,96 Q54,94 56,96 Q58,98 60,96 Q62,94 64,96 Q66,98 68,96 Q70,94 72,96 Q74,98 76,96 Q78,94 80,96 Q82,98 84,96 Q86,94 88,96 Q90,98 92,96" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M10,97.5 Q12,99 14,97.5 M18,97.5 Q20,99 22,97.5 M26,97.5 Q28,99 30,97.5 M34,97.5 Q36,99 38,97.5 M42,97.5 Q44,99 46,97.5 M50,97.5 Q52,99 54,97.5 M58,97.5 Q60,99 62,97.5 M66,97.5 Q68,99 70,97.5 M74,97.5 Q76,99 78,97.5 M82,97.5 Q84,99 86,97.5 M90,97.5 Q92,99 94,97.5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M4,8 Q2,10 4,12 Q6,14 4,16 Q2,18 4,20 Q6,22 4,24 Q2,26 4,28 Q6,30 4,32 Q2,34 4,36 Q6,38 4,40 Q2,42 4,44 Q6,46 4,48 Q2,50 4,52 Q6,54 4,56 Q2,58 4,60 Q6,62 4,64 Q2,66 4,68 Q6,70 4,72 Q2,74 4,76 Q6,78 4,80 Q2,82 4,84 Q6,86 4,88 Q2,90 4,92" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M2.5,10 Q1,12 2.5,14 M2.5,18 Q1,20 2.5,22 M2.5,26 Q1,28 2.5,30 M2.5,34 Q1,36 2.5,38 M2.5,42 Q1,44 2.5,46 M2.5,50 Q1,52 2.5,54 M2.5,58 Q1,60 2.5,62 M2.5,66 Q1,68 2.5,70 M2.5,74 Q1,76 2.5,78 M2.5,82 Q1,84 2.5,86 M2.5,90 Q1,92 2.5,94" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M96,8 Q98,10 96,12 Q94,14 96,16 Q98,18 96,20 Q94,22 96,24 Q98,26 96,28 Q94,30 96,32 Q98,34 96,36 Q94,38 96,40 Q98,42 96,44 Q94,46 96,48 Q98,50 96,52 Q94,54 96,56 Q98,58 96,60 Q94,62 96,64 Q98,66 96,68 Q94,70 96,72 Q98,74 96,76 Q94,78 96,80 Q98,82 96,84 Q94,86 96,88 Q98,90 96,92" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M97.5,10 Q99,12 97.5,14 M97.5,18 Q99,20 97.5,22 M97.5,26 Q99,28 97.5,30 M97.5,34 Q99,36 97.5,38 M97.5,42 Q99,44 97.5,46 M97.5,50 Q99,52 97.5,54 M97.5,58 Q99,60 97.5,62 M97.5,66 Q99,68 97.5,70 M97.5,74 Q99,76 97.5,78 M97.5,82 Q99,84 97.5,86 M97.5,90 Q99,92 97.5,94" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.4} />
                                    <path d="M8,8 Q6,10 8,12 Q10,14 12,12 Q14,10 12,8 Q10,6 8,8 M8,8 Q10,6 12,8 M8,8 Q6,10 8,12 M12,8 Q14,10 12,12" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <circle cx="10" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="10" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />
                                    <path d="M92,8 Q94,10 92,12 Q90,14 88,12 Q86,10 88,8 Q90,6 92,8 M92,8 Q90,6 88,8 M92,8 Q94,10 92,12 M88,8 Q86,10 88,12" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <circle cx="90" cy="10" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="90" cy="10" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />
                                    <path d="M8,92 Q6,90 8,88 Q10,86 12,88 Q14,90 12,92 Q10,94 8,92 M8,92 Q10,94 12,92 M8,92 Q6,90 8,88 M12,92 Q14,90 12,88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <circle cx="10" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="10" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />
                                    <path d="M92,92 Q94,90 92,88 Q90,86 88,88 Q86,90 88,92 Q90,94 92,92 M92,92 Q90,94 88,92 M92,92 Q94,90 92,88 M88,92 Q86,90 88,88" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.7} />
                                    <circle cx="90" cy="90" r="3" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <circle cx="90" cy="90" r="5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.3} />
                                    <path d="M15,8 Q18,6 20,8 Q22,10 20,13 Q18,15 15,13 Q13,10 15,8" fill={frameColor} opacity="0.2" />
                                    <path d="M85,8 Q82,6 80,8 Q78,10 80,13 Q82,15 85,13 Q87,10 85,8" fill={frameColor} opacity="0.2" />
                                    <path d="M15,92 Q18,94 20,92 Q22,90 20,87 Q18,85 15,87 Q13,90 15,92" fill={frameColor} opacity="0.2" />
                                    <path d="M85,92 Q82,94 80,92 Q78,90 80,87 Q82,85 85,87 Q87,90 85,92" fill={frameColor} opacity="0.2" />
                                    <circle cx="50" cy="3" r="2" fill={frameColor} />
                                    <circle cx="50" cy="97" r="2" fill={frameColor} />
                                    <circle cx="3" cy="50" r="2" fill={frameColor} />
                                    <circle cx="97" cy="50" r="2" fill={frameColor} />
                                    <path d="M25,5 Q27,3 29,5 M71,5 Q73,3 75,5" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M25,95 Q27,97 29,95 M71,95 Q73,97 75,95" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M5,25 Q3,27 5,29 M5,71 Q3,73 5,75" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                    <path d="M95,25 Q97,27 95,29 M95,71 Q97,73 95,75" fill="none" stroke={frameColor} strokeWidth={frameThickness * 0.5} />
                                </>
                            )
                        };

                        return (
                            <svg
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none'
                                }}
                            >
                                {framePatterns[frameStyle] || framePatterns.vintage_border}
                            </svg>
                        );
                    })()}

                    {/* Border Pattern Overlay */}
                    {(() => {
                        const borderPattern = template.design?.borderPattern || 'none';
                        const borderColor = template.design?.borderColor || '#000000';

                        if (borderPattern === 'none') return null;

                        const patterns = {
                            dots: 'M0,5 L0,5 M10,5 L10,5 M20,5 L20,5 M30,5 L30,5 M40,5 L40,5 M50,5 L50,5 M60,5 L60,5 M70,5 L70,5 M80,5 L80,5 M90,5 L90,5 M100,5 L100,5',
                            waves: 'M0,5 Q5,0 10,5 T20,5 T30,5 T40,5 T50,5 T60,5 T70,5 T80,5 T90,5 T100,5',
                            zigzag: 'M0,8 L5,2 L10,8 L15,2 L20,8 L25,2 L30,8 L35,2 L40,8 L45,2 L50,8 L55,2 L60,8 L65,2 L70,8 L75,2 L80,8 L85,2 L90,8 L95,2 L100,8',
                            chain: 'M0,5 Q5,0 10,5 Q15,10 20,5 Q25,0 30,5 Q35,10 40,5 Q45,0 50,5 Q55,10 60,5 Q65,0 70,5 Q75,10 80,5 Q85,0 90,5 Q95,10 100,5'
                        };

                        return (
                            <>
                                <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', left: '5px', width: 'calc(100% - 10px)', height: '15px' }}>
                                    <path d={patterns[borderPattern]} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                </svg>
                                <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: 'absolute', bottom: '5px', left: '5px', width: 'calc(100% - 10px)', height: '15px' }}>
                                    <path d={patterns[borderPattern]} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                </svg>
                                <svg viewBox="0 0 10 100" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', left: '5px', width: '15px', height: 'calc(100% - 10px)' }}>
                                    <path d={patterns[borderPattern].replace(/M0,5/g, 'M5,0').replace(/L(\d+),5/g, 'L5,$1').replace(/Q(\d+),(\d+)/g, 'Q$2,$1').replace(/T(\d+),5/g, 'T5,$1')} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                </svg>
                                <svg viewBox="0 0 10 100" preserveAspectRatio="none" style={{ position: 'absolute', top: '5px', right: '5px', width: '15px', height: 'calc(100% - 10px)' }}>
                                    <path d={patterns[borderPattern].replace(/M0,5/g, 'M5,0').replace(/L(\d+),5/g, 'L5,$1').replace(/Q(\d+),(\d+)/g, 'Q$2,$1').replace(/T(\d+),5/g, 'T5,$1')} fill="none" stroke={borderColor} strokeWidth="1.5" />
                                </svg>
                            </>
                        );
                    })()}
                    {/* Corner Decorations */}
                    {template.design?.cornerDecoration?.enabled && (
                        <>
                            {console.log('Corner decoration enabled:', template.design.cornerDecoration)}
                            {/* Top Left Corner */}
                            <div style={{ position: 'absolute', top: '10px', left: '10px' }}
                                dangerouslySetInnerHTML={{
                                    __html: template.design.cornerDecoration.customImage
                                        ? `<img src="${config.baseUrl}${template.design.cornerDecoration.customImage}" 
                                            style="width: ${template.design.cornerDecoration.size}px; height: ${template.design.cornerDecoration.size}px;" />`
                                        : getCornerDecoration(
                                            template.design.cornerDecoration.type,
                                            template.design.cornerDecoration.color,
                                            template.design.cornerDecoration.size
                                        )
                                }}
                            />
                            {/* Top Right Corner */}
                            <div style={{ position: 'absolute', top: '10px', right: '10px', transform: 'scaleX(-1)' }}
                                dangerouslySetInnerHTML={{
                                    __html: template.design.cornerDecoration.customImage
                                        ? `<img src="${config.baseUrl}${template.design.cornerDecoration.customImage}" 
                                            style="width: ${template.design.cornerDecoration.size}px; height: ${template.design.cornerDecoration.size}px;" />`
                                        : getCornerDecoration(
                                            template.design.cornerDecoration.type,
                                            template.design.cornerDecoration.color,
                                            template.design.cornerDecoration.size
                                        )
                                }}
                            />
                            {/* Bottom Left Corner */}
                            <div style={{ position: 'absolute', bottom: '10px', left: '10px', transform: 'scaleY(-1)' }}
                                dangerouslySetInnerHTML={{
                                    __html: template.design.cornerDecoration.customImage
                                        ? `<img src="${config.baseUrl}${template.design.cornerDecoration.customImage}" 
                                            style="width: ${template.design.cornerDecoration.size}px; height: ${template.design.cornerDecoration.size}px;" />`
                                        : getCornerDecoration(
                                            template.design.cornerDecoration.type,
                                            template.design.cornerDecoration.color,
                                            template.design.cornerDecoration.size
                                        )
                                }}
                            />
                            {/* Bottom Right Corner */}
                            <div style={{ position: 'absolute', bottom: '10px', right: '10px', transform: 'scale(-1)' }}
                                dangerouslySetInnerHTML={{
                                    __html: template.design.cornerDecoration.customImage
                                        ? `<img src="${config.baseUrl}${template.design.cornerDecoration.customImage}" 
                                            style="width: ${template.design.cornerDecoration.size}px; height: ${template.design.cornerDecoration.size}px;" />`
                                        : getCornerDecoration(
                                            template.design.cornerDecoration.type,
                                            template.design.cornerDecoration.color,
                                            template.design.cornerDecoration.size
                                        )
                                }}
                            />
                        </>
                    )}

                    {/* Header with Logo */}
                    <div
                        style={{
                            textAlign: template.design?.layoutSettings?.headerAlign || 'center',
                            marginBottom: `${template.design?.layoutSettings?.headerSpacing || 25}px`
                        }}
                    >
                        {template.header?.logo && (
                            <div
                                style={{
                                    marginBottom: '15px',
                                    display: 'flex',
                                    justifyContent: (() => {
                                        const align = template.design?.layoutSettings?.logoAlign || 'center';
                                        if (align === 'left') return 'flex-start';
                                        if (align === 'right') return 'flex-end';
                                        return 'center';
                                    })(),
                                    marginTop: `${template.design?.layoutSettings?.logoOffsetTop || 0}px`,
                                    position: 'relative',
                                    transform: `translate(${template.design?.layoutSettings?.logoOffsetX || 0}px, ${template.design?.layoutSettings?.logoOffsetY || 0}px)`
                                }}
                            >
                                <img
                                    src={`${config.baseUrl}${template.header.logo}`}
                                    alt=""
                                    crossOrigin="anonymous"
                                    style={{
                                        maxHeight: `${template.design?.layoutSettings?.logoMaxHeight || 100}px`,
                                        maxWidth: `${template.design?.layoutSettings?.logoMaxWidth || 200}px`,
                                        height: 'auto',
                                        width: 'auto',
                                        display: 'block',
                                        margin: '0 auto',
                                        objectFit: 'contain'
                                    }}
                                    onError={(e) => {
                                        console.error('Logo failed to load:', e.target.src);
                                        console.error('Error details:', e);
                                    }}
                                    onLoad={(e) => {
                                        console.log('Logo loaded successfully:', e.target.src);
                                        console.log('Image dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                                    }}
                                />
                            </div>
                        )}
                        <h1 style={{
                            fontSize: `${template.header?.instituteNameFont?.size || 40}px`,
                            color: template.header?.instituteNameFont?.color || '#1890ff',
                            margin: '0',
                            fontWeight: 'bold',
                            fontFamily: template.header?.instituteNameFont?.family || 'Arial',
                            textAlign: template.design?.layoutSettings?.headerAlign || 'center',
                            position: 'relative',
                            transform: `translate(${template.design?.layoutSettings?.headerOffsetX || 0}px, ${template.design?.layoutSettings?.headerOffsetY || 0}px)`
                        }}>
                            {template.header?.instituteName || t('languageInstituteManagementSystem')}
                        </h1>
                    </div>

                    {/* Certificate Title */}
                    <div
                        style={{
                            textAlign: template.design?.layoutSettings?.titleAlign || 'center',
                            marginBottom: `${template.design?.layoutSettings?.titleSpacing || 35}px`,
                            position: 'relative',
                            transform: `translate(${template.design?.layoutSettings?.titleOffsetX || 0}px, ${template.design?.layoutSettings?.titleOffsetY || 0}px)`
                        }}
                    >
                        <h2 style={{
                            fontSize: `${template.body?.title?.font?.size || 32}px`,
                            color: template.body?.title?.font?.color || '#333',
                            margin: '0',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            fontFamily: template.body?.title?.font?.family || 'Arial',
                            textAlign: template.design?.layoutSettings?.titleAlign || 'center'
                        }}>
                            {template.body?.title?.text || t('certificates')}
                        </h2>
                    </div>

                    {/* Certificate Body */}
                    <div style={{
                        fontSize: `${template.body?.text?.font?.size || 18}px`,
                        fontWeight: template.body?.text?.font?.weight || 'normal',
                        lineHeight: '1.8',
                        textAlign: template.design?.layoutSettings?.bodyAlign || 'center',
                        marginBottom: `${template.design?.layoutSettings?.bodySpacing || 35}px`,
                        color: template.body?.text?.font?.color || '#333',
                        fontFamily: template.body?.text?.font?.family || 'Arial',
                        whiteSpace: 'pre-line',
                        maxWidth: `${template.design?.layoutSettings?.bodyMaxWidth || 600}px`,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        position: 'relative',
                        transform: `translate(${template.design?.layoutSettings?.bodyOffsetX || 0}px, ${template.design?.layoutSettings?.bodyOffsetY || 0}px)`
                    }}>
                        {processTextWithStyles(template.body?.text?.template)}
                    </div>

                    {/* Badges */}
                    {template.badges?.map((badge, index) => (
                        badge.url && (
                            <div
                                key={index}
                                style={{
                                    position: 'absolute',
                                    left: `${badge.x || 50}%`,
                                    top: `${badge.y || 50}%`,
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 10
                                }}
                            >
                                <img
                                    src={`${config.baseUrl}${badge.url}`}
                                    alt={`Badge ${index + 1}`}
                                    style={{
                                        width: `${badge.size || 80}px`,
                                        height: `${badge.size || 80}px`,
                                        objectFit: 'contain'
                                    }}
                                    onError={(e) => {
                                        console.error('Badge failed to load:', e.target.src);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )
                    ))}

                    {/* Watermark */}
                    {template.watermark?.image && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                opacity: template.watermark.opacity || 0.1,
                                zIndex: 1,
                                pointerEvents: 'none'
                            }}
                        >
                            <img
                                src={`${config.baseUrl}${template.watermark.image}`}
                                alt="Watermark"
                                style={{
                                    width: `${template.watermark.size || 300}px`,
                                    height: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    )}


                    {/* Footer Section */}
                    <div style={{
                        position: 'absolute',
                        bottom: '15mm',
                        left: '15mm',
                        right: '15mm',
                        transform: `translate(${template.design?.layoutSettings?.footerOffsetX || 0}px, ${template.design?.layoutSettings?.footerOffsetY || 0}px)`
                    }}>
                        {/* Signature Slots */}
                        {template.signatureSlots?.length > 0 && (
                            <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-around', maxWidth: `${template.design?.layoutSettings?.footerMaxWidth || 600}px`, margin: '0 auto 30px auto' }}>
                                {template.signatureSlots.map((slot, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            textAlign: 'center',
                                            flex: slot.position === 'center' ? '0 0 auto' : '1',
                                            maxWidth: '200px'
                                        }}
                                    >
                                        <div style={{ borderTop: '1px solid #000', paddingTop: '5px' }}>
                                            {slot.showSignerName && slot.signerName && (
                                                <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '3px' }}>
                                                    {slot.signerName}
                                                </div>
                                            )}
                                            {slot.showLabel && <span style={{ fontSize: '14px' }}>{slot.label}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Date */}
                        {template.footer?.showDate && (
                            <div style={{ textAlign: template.design?.layoutSettings?.footerAlign || 'center', width: '100%' }}>
                                <div style={{ fontSize: '14px', marginBottom: '5px' }}>
                                    {dayjs(certificate.issueDate).format('YYYY-MM-DD')}
                                </div>
                            </div>
                        )}

                        {/* Certificate Number and Verification Code */}
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <p style={{ fontSize: '8px', color: '#666', margin: '0' }}>
                                {t('certificateNo')}: {certificate.certificateNumber}
                            </p>
                            <p style={{ fontSize: '6px', color: '#999', margin: '5px 0 0 0' }}>
                                {t('verificationCode')}: {certificate.verificationCode}
                            </p>
                        </div>
                    </div>

                    {/* Watermark for classic/modern layouts */}
                    {(template.design?.layout === 'classic' || template.design?.layout === 'modern') && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-45deg)',
                            opacity: 0.03,
                            fontSize: '120px',
                            fontWeight: 'bold',
                            color: template.design?.borderColor || '#1890ff',
                            pointerEvents: 'none',
                            zIndex: 0
                        }}>
                            {t('certificates')}
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div>
            <h1 style={{ marginBottom: 16 }}>{t('certificates')}</h1>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Select
                        placeholder={t('filterByCourse')}
                        style={{ width: '100%' }}
                        allowClear
                        onChange={(value) => {
                            setSelectedCourse(value);
                            setSelectedRowKeys([]);
                        }}
                        value={selectedCourse}
                    >
                        {coursesData?.map(course => (
                            <Option key={course._id} value={course._id}>
                                {course.name} ({course.code})
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={16} style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleSelectAll}>
                            {t('selectAll')}
                        </Button>
                        <Button onClick={handleDeselectAll} disabled={selectedRowKeys.length === 0}>
                            {t('deselectAll')}
                        </Button>
                        <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            onClick={handlePrintSelected}
                            disabled={selectedRowKeys.length === 0}
                        >
                            {t('printSelected')} ({selectedRowKeys.length})
                        </Button>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={handleDeleteCourseCertificates}
                            disabled={!selectedCourse || !filteredCertificates?.length}
                            loading={deleteMutation.isLoading}
                        >
                            {t('deleteCertificates') || 'Delete All'} {selectedCourse && filteredCertificates?.length ? `(${filteredCertificates.length})` : ''}
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filteredCertificates}
                rowKey="_id"
                loading={isLoading}
                rowSelection={rowSelection}
            />

            <Modal
                title={null}
                open={previewModalVisible}
                onCancel={() => {
                    setPreviewModalVisible(false);
                    setSelectedCertificate(null);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setPreviewModalVisible(false);
                        setSelectedCertificate(null);
                    }}>
                        {t('close')}
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={triggerPrint}
                        loading={isPrinting}
                    >
                        {t('print')}
                    </Button>,
                ]}
                width={1200}
                destroyOnHidden={false}
            >
                <div ref={printRef}>
                    <CertificateTemplate certificate={selectedCertificate} />
                </div>
            </Modal>

            {/* Hidden single certificate for direct print */}
            <div style={{ display: 'none' }}>
                <div ref={directPrintRef}>
                    <CertificateTemplate certificate={selectedCertificate} />
                </div>
            </div>

            {/* Hidden bulk print container */}
            <div style={{ display: 'none' }}>
                <div ref={bulkPrintRef}>
                    <style>
                        {`
                            @media print {
                                @page {
                                    size: A4 landscape;
                                    margin: 0;
                                }
                                body {
                                    margin: 0;
                                    padding: 0;
                                }
                                .certificate-page {
                                    page-break-after: always;
                                    page-break-inside: avoid;
                                }
                                .certificate-page:last-child {
                                    page-break-after: auto;
                                }
                            }
                        `}
                    </style>
                    {selectedCertificates.map((cert, index) => (
                        <div
                            key={cert._id}
                            className="certificate-page"
                            style={{
                                pageBreakAfter: index < selectedCertificates.length - 1 ? 'always' : 'auto',
                                pageBreakInside: 'avoid'
                            }}
                        >
                            <CertificateTemplate certificate={cert} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Certificates;
